import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { parseFractionLikeInput as parseFraction } from "../../../../components/inputs/FractionInput";
import CirclePolygonAnglesDiagram from "../../../../components/diagrams/CirclePolygonAnglesDiagram";

const DEC_TOL = 0.1;
const ANGLES = [24, 28, 32, 35, 38, 40, 44, 48, 52, 55, 58, 60, 64, 68];

function rawText(value) {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return String(value.raw ?? value.value ?? value.input ?? "");
}

function normalize(raw) {
  return String(raw ?? "").trim().replace(/\u2212/g, "-").replace(/\s+/g, "").replace(",", ".");
}

function pretty(raw) {
  return String(raw ?? "").trim().replace(".", "{,}");
}

export function generateTask() {
  const given = ANGLES[Math.floor(Math.random() * ANGLES.length)];
  const givenVertex = Math.random() < 0.5 ? "A" : "B";
  const unknownVertex = givenVertex === "A" ? "B" : "A";
  const cDeg = givenVertex === "A" ? 360 - 2 * given : 180 + 2 * given;
  return { type: "FindAngleInSemicircleTriangleTask", data: { given, givenVertex, unknownVertex, cDeg, answer: 90 - given } };
}

export function renderTask({ data }) {
  const { given, givenVertex, unknownVertex, cDeg } = data;
  return (
    <div style={{ marginTop: 8, marginBottom: 8 }}>
      {"Na rysunku punkt "}<InlineMath math="O" />{" jest \u015brodkiem okr\u0119gu, a punkty "}<InlineMath math="A" />{" , "}<InlineMath math="O" />{" i "}<InlineMath math="B" />{" le\u017c\u0105 na jednej prostej. "}
      {"Punkt "}<InlineMath math="C" />{" le\u017cy na okr\u0119gu. K\u0105t przy wierzcho\u0142ku "}<InlineMath math={givenVertex} />{" ma miar\u0119 "}<InlineMath math={`${given}^{\\circ}`} />{". "}
      {"Oblicz miar\u0119 k\u0105ta "}<span style={{ whiteSpace: "nowrap" }}><InlineMath math={"\\alpha"} />{" w wierzcho\u0142ku "}<InlineMath math={unknownVertex} />{"."}</span>
      <div style={{ marginTop: 12, display: "flex", justifyContent: "center" }}>
        <CirclePolygonAnglesDiagram scenario="semicircle" givenDeg={{ value: given, vertex: givenVertex, cDeg }} />
      </div>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return <FractionInput value={rawText(userAnswer)} onChange={(value) => onChange({ raw: rawText(value) })} disabled={disabled} labelLatex={"\\alpha"} suffixLatex={"^\\circ"} placeholder="np. 42" />;
}

export function checkAnswer({ data }, userAnswer) {
  const raw = rawText(userAnswer);
  if (!raw.trim()) return false;
  const value = parseFraction(normalize(raw));
  return Number.isFinite(value) && Math.abs(value - data.answer) <= DEC_TOL;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`\\alpha=${data.answer}^{\\circ}`} />;
}

export function renderExplanation({ data }) {
  const { given, unknownVertex, answer } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <strong>Krok 1.</strong>{" Punkty "}<InlineMath math="A" />{" , "}<InlineMath math="O" />{" i "}<InlineMath math="B" />{" le\u017c\u0105 na jednej prostej, a "}<InlineMath math="O" />{" jest \u015brodkiem okr\u0119gu. Zatem odcinek "}<InlineMath math="AB" />{" jest \u015brednic\u0105."}
      <br />
      {"K\u0105t wpisany oparty na \u015brednicy jest prosty, wi\u0119c:"}
      <BlockMath math={"\\angle C=90^{\\circ}"} />
      <div style={{ margin: "10px 0", display: "flex", justifyContent: "center" }}>
        <CirclePolygonAnglesDiagram
          scenario="semicircle"
          givenDeg={{ value: given, vertex: data.givenVertex, cDeg: data.cDeg }}
          showRightAngleAtC
        />
      </div>
      <strong>Krok 2.</strong>{" Korzystamy z sumy k\u0105t\u00f3w w tr\u00f3jk\u0105cie."}
      <BlockMath math={`\\angle ${unknownVertex}=180^{\\circ}-90^{\\circ}-${given}^{\\circ}=${answer}^{\\circ}`} />
      <div style={{ marginTop: 10 }}>{"Odpowied\u017a: "}<InlineMath math={`\\alpha=\\angle ${unknownVertex}=${answer}^{\\circ}`} />{"."}</div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const raw = rawText(userAnswer);
  if (!raw.trim()) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`\\alpha=${pretty(raw)}^{\\circ}`} />;
}

const taskModule = { generateTask, renderTask, renderInput, checkAnswer, renderAnswer, renderExplanation, renderUserAnswer };
export default taskModule;
